import { useState, useEffect } from 'react';
import {
    useNavigate,
    useLocation,
} from 'react-router-dom';
import { Card, Typography } from '@mui/material';
import styled from 'styled-components';

import { IconChevronLeft, IconEdit, IconDeviceFloppy, IconCancel, IconTrash } from '@tabler/icons-react';

import { useRolesHook } from '../../Hooks/Maintenance/Accessibilities/rolesHooks';

import { AdminUserDetailsInterface } from '../../Models/AdminUserControl/AdminUserControl';
import { Authentication } from "../../Models/Authentication/Authentication";
import { Vendor } from '../../Models/Maintenance/Vendors/Vendor';

import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import StatusHelper from '../../Utilities/Helpers/StatusHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import AccessibilityHelper from '../../Utilities/Helpers/AccessibilityHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';

import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { ViewType } from '../../Utilities/Enums/ViewTypeEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { AdminUserField, AdminUserAddressField, AdminUserProfileField, RolesField, VendorField, AuthenticationField } from '../../Utilities/Enums/ParametersEnum';
import { AdminModules } from '../../Utilities/Enums/AdminModulesEnum';
import { ActiveStatusEnum } from '../../Utilities/Enums/StatusEnums';
import { RoleTypeEnum } from '../../Utilities/Enums/RolesEnums';

import FormTextField from '../../CustomViews/Elements/FormTextField';
import AutoCompleteDropdown from '../../CustomViews/Elements/AutoCompleteDropdown';
import CompanyDropdown from '../../CustomViews/Elements/AutoComplete/CompanyDropdown';
import VendorsDropdown from '../../CustomViews/Elements/AutoComplete/VendorsDropdown';
import RolesDropdown from '../../CustomViews/Elements/AutoComplete/RolesDropdown';
import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import CustomUploadPhoto from '../../CustomViews/Elements/CustomUploadPhoto/CustomUploadPhoto';

const FieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 30px;
`;

const FieldItemCard = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
`;

const FieldItemName = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    color: #878a99 !important;
    font-weight: 600 !important;
    font-size: 0.80rem !important;
    margin: 0px 0px 5px 0px !important;
`;

const FieldItemValue = styled(Typography) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    font-weight: 500 !important;
    font-size: 16px !important;
`;


const FormFieldItemsContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
`;

export default function AdminUserDetailsForm(props: AdminUserDetailsInterface) {
    //Variables
    let navigate = useNavigate();
    let location = useLocation();
    let userData: Authentication = LocalStorageHelper.getUserData();
    let { adminuserid, viewType, setviewType, setopenDelete, settoDeleteItem, previewData, formData, setformData, errorMessages, setErrorMessages, DropdownData, dropdownFetchStatus, handleSubmit, routePath } = props;
    let requiredFields = RequiredParametersHelper.adminUserControl();

    //Use States
    const [queryStr, setQueryStr] = useState({
        pageSize: 2000,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
    });

    const q_queryKey = [CachingEnum.ACCESSIBILITIES, queryStr]
    const { data, fetchStatus } = useRolesHook(q_queryKey, queryStr);

    const nameProps = {
        name: AdminUserProfileField.NAME,
        title: 'POC Name',
        fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.NAME] ?? "",
        type: 'text',
        required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.NAME)?.keyName != null,
        errorMessages: errorMessages,
        handleOnChange: (selectedValue: any) => {
            let _formData = { ...formData, [AdminUserField.PROFILE]: {...formData?.[AdminUserField.PROFILE], [AdminUserProfileField.NAME]: selectedValue }};

            updateData(_formData);
        },
    },
        billingAddressProps = {
            name: AdminUserAddressField.STREET_ADDRESS,
            title: 'Billing Address',
            fieldValue: formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.STREET_ADDRESS] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.ADDRESS)?.fields?.find((f: any) => f.keyName == AdminUserAddressField.STREET_ADDRESS)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [AdminUserField.ADDRESS]: {...formData?.[AdminUserField.ADDRESS], [AdminUserAddressField.STREET_ADDRESS]: selectedValue }};

                updateData(_formData);
            },
        },
        roleProps = {
            name: AdminUserField.ROLE_PUBLIC_ID,
            title: 'Role',
            fieldValue: formData?.[AdminUserField.ROLE_PUBLIC_ID] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.ROLE_PUBLIC_ID)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let isVendorAdmin = selectedValue?.[RolesField.TYPE] == RoleTypeEnum.VENDOR_ADMIN;

                if(setformData) {
                    setformData((prev: any) => ({
                        ...prev,
                        [AdminUserField.ROLE_PUBLIC_ID]: selectedValue?.id ?? '',
                        [AdminUserField.ROLE]: selectedValue ?? null,
                        [AdminUserField.PROFILE]: {
                            ...prev?.[AdminUserField.PROFILE],

                            [AdminUserProfileField.COMPANY_PUBLIC_ID]: previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.COMPANY_PUBLIC_ID] ?? null,
                            [AdminUserProfileField.REGISTERED_COMPANY]: previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.REGISTERED_COMPANY] ?? "",

                            [AdminUserProfileField.VENDOR_IDS]: (isVendorAdmin ? previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.VENDOR_IDS] : []),
                            [AdminUserProfileField.VENDORS]: (isVendorAdmin ? previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.VENDORS] : []),
                        }
                     }));
                }
            },
        },
        cityProps = {
            name: AdminUserAddressField.CITY,
            title: 'City',
            fieldValue: formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.CITY] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserAddressField.CITY)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [AdminUserField.ADDRESS]: {...formData?.[AdminUserField.ADDRESS], [AdminUserAddressField.CITY]: selectedValue }};

                updateData(_formData);
            },
        },
        stateProps = {
            name: AdminUserAddressField.STATE,
            title: 'State',
            fieldValue: formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.STATE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserAddressField.STATE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [AdminUserField.ADDRESS]: {...formData?.[AdminUserField.ADDRESS], [AdminUserAddressField.STATE]: selectedValue }};

                updateData(_formData);
            },
        },
        contPhoneProps = {
            name: AdminUserProfileField.CONTACT_NUMBER,
            title: 'Contact Number (Office)',
            fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CONTACT_NUMBER] ?? "",
            type: 'number',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.CONTACT_NUMBER)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.PROFILE]: {...prev?.[AdminUserField.PROFILE], [AdminUserProfileField.CONTACT_NUMBER]: selectedValue }}));
                }
            },
        },
        contEmailAddressProps = {
            name: AdminUserProfileField.EMAIL,
            title: 'Email (Office)',
            fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.EMAIL] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.EMAIL)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.PROFILE]: {...prev?.[AdminUserField.PROFILE], [AdminUserProfileField.EMAIL]: selectedValue }}));
                }
            },
        },
        postalCodeProps = {
            name: AdminUserAddressField.ZIP_CODE,
            title: 'Postal Code',
            fieldValue: formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.ZIP_CODE] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserAddressField.ZIP_CODE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [AdminUserField.ADDRESS]: {...formData?.[AdminUserField.ADDRESS], [AdminUserAddressField.ZIP_CODE]: selectedValue }};

                updateData(_formData);
            },
        },
        countryProps = {
            name: AdminUserAddressField.COUNTRY,
            title: 'Country',
            fieldValue: formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.COUNTRY] ?? "",
            options: DropdownData?.[CachingEnum.COUNTRY_LIST] ?? [],
            isLoadingDropdown: dropdownFetchStatus ?? false,
            required: requiredFields?.find((field: any) => field.keyName == AdminUserAddressField.COUNTRY)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                let _formData = { ...formData, [AdminUserField.ADDRESS]: {...formData?.[AdminUserField.ADDRESS], [AdminUserAddressField.COUNTRY]: selectedValue?.id ?? '' }};

                updateData(_formData);
            },
        },
        registeredCompanyProps = {
            name: AdminUserProfileField.COMPANY_PUBLIC_ID,
            title: 'Registered Company',
            disabled: (userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.SUPER_USER) ? false : (viewType != ViewType.ADD),
            fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.COMPANY_PUBLIC_ID] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.COMPANY_PUBLIC_ID)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({
                        ...prev,
                        [AdminUserField.PROFILE]: {
                            ...formData?.[AdminUserField.PROFILE],
                            [AdminUserProfileField.COMPANY_PUBLIC_ID]: selectedValue?.id ?? null,
                            [AdminUserProfileField.REGISTERED_COMPANY]: selectedValue?.name ?? '',
                        }
                    }));
                }
            },
        },
        vendorProps = {
            name: AdminUserProfileField.VENDOR_IDS,
            title: 'Vendors',
            fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.VENDOR_IDS] ?? [],
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.VENDOR_IDS)?.keyName != null,
            multiple: true,
            disabled: (userData?.[AuthenticationField.ROLE_TYPE] == RoleTypeEnum.SUPER_USER) ? false : (viewType != ViewType.ADD),
            errorMessages: errorMessages,
            isUserVendorsOnly: userData?.[AuthenticationField.ROLE_TYPE] != RoleTypeEnum.SUPER_USER,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({
                        ...prev,
                        [AdminUserField.PROFILE]: {
                            ...formData?.[AdminUserField.PROFILE],
                            [AdminUserProfileField.VENDOR_IDS]: selectedValue?.map((item: any) => { return item.id }) ?? [],
                            [AdminUserProfileField.VENDORS]: selectedValue ?? [],
                        }
                    }));
                }
            },
        },
        taxNumberProps = {
            name: AdminUserProfileField.TAX_NO,
            title: 'Tax Number',
            fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.TAX_NO] ?? "",
            type: 'text',
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.TAX_NO)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.PROFILE]: {...prev?.[AdminUserField.PROFILE], [AdminUserProfileField.TAX_NO]: selectedValue }}));
                }
            },
        },
        localeProps = {
            name: AdminUserProfileField.CURRENCY_CODE,
            title: 'Locale',
            fieldValue: formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE] ?? "",
            options: DropdownData?.[CachingEnum.PRICE_SETTINGS_TAB_LIST] ?? [],
            isLoadingDropdown: dropdownFetchStatus ?? false,
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.PROFILE)?.fields?.find((f: any) => f.keyName == AdminUserProfileField.CURRENCY_CODE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.PROFILE]: {...prev?.[AdminUserField.PROFILE], [AdminUserProfileField.CURRENCY_CODE]: selectedValue?.id ?? '' }}));
                }
            },
        },
        statusProps = {
            name: AdminUserField.STATUS,
            title: 'Status',
            fieldValue: formData?.[AdminUserField.STATUS] ?? "",
            options: DropdownData?.[CachingEnum.STATUS_LIST] ?? [],
            isLoadingDropdown: dropdownFetchStatus ?? false,
            required: requiredFields?.find((field: any) => field.keyName == AdminUserField.STATUS)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                if(setformData) {
                    setformData((prev: any) => ({ ...prev, [AdminUserField.STATUS]: selectedValue?.id ?? '' }));
                }
            },
        };

    //Event Handlers
    const updateData = async (_formData: any) => {
        let _streetAddress = _formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.STREET_ADDRESS] ?? "";
        let _city = _formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.CITY] ?? "";
        let _state = _formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.STATE] ?? "";
        let _zipCode = _formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.ZIP_CODE] ?? "";
        let _country = DropdownData?.[CachingEnum.COUNTRY_LIST]?.find((x: any) => x.id == (_formData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.COUNTRY] ?? 0))?.name ?? "";
        let _fullAddress = _streetAddress ?? "";
        let _fullName = _formData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.NAME]?.split(" ") ?? [];
        let _lastName = _fullName?.length > 1 ? _fullName.pop() : '';
        let _firstName = _fullName?.length <= 1 ? _fullName[0] : _fullName.join(' ');
        

        _fullAddress += _city ? " " + _city : "";
        _fullAddress += _state ? " " + _state : "";
        _fullAddress += _zipCode ? " " + _zipCode : "";
        _fullAddress += _country ? " " + _country : "";

        if(setformData) {
            setformData({
                ..._formData,
                [AdminUserField.ADDRESS]: {..._formData?.[AdminUserField.ADDRESS], [AdminUserAddressField.FULL_ADDRESS]: _fullAddress},
                [AdminUserField.PROFILE]: {
                    ..._formData?.[AdminUserField.PROFILE],
                    [AdminUserProfileField.FIRST_NAME]: _firstName,
                    [AdminUserProfileField.LAST_NAME]: _lastName,
                },
            });
        }
    }

    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.ADMIN_USER_CONTROL);
                }} />

            {routePath == RouteEnum.ADMIN_USER_DETAILS && viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(AdminModules.ADMIN_USER_DELETE) && <CustomButton
                btnType={ButtonType.DELETE}
                title='Delete'
                startIcon={<IconTrash />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    if (setopenDelete) {
                        setopenDelete(true);
                    }

                    if(settoDeleteItem) {
                        settoDeleteItem(previewData);
                    }
                }} />}
            {viewType == ViewType.EDIT && <CustomButton
                btnType={ButtonType.CANCEL}
                title='Cancel'
                startIcon={<IconCancel />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    // navigate(`${RouteEnum.ADMIN_USER_DETAILS}?id=${adminuserid}&view=${ViewType.PREVIEW}`);
                    navigate(`${routePath}?id=${adminuserid}&view=${ViewType.PREVIEW}`, { replace: true });

                    if(setviewType) {
                        setviewType(ViewType.PREVIEW);
                    }
                }} />}
            {viewType == ViewType.PREVIEW && AccessibilityHelper.hasAccess(routePath == RouteEnum.ADMIN_USER_DETAILS ? AdminModules.ADMIN_USER_EDIT : AdminModules.MY_PROFILE_EDIT) && <CustomButton
                btnType={ButtonType.PRIMARY}
                title='Edit'
                startIcon={<IconEdit />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    // navigate(`${RouteEnum.ADMIN_USER_DETAILS}?id=${adminuserid}&view=${ViewType.EDIT}`);
                    navigate(`${routePath}?id=${adminuserid}&view=${ViewType.EDIT}`, { replace: true });
                    
                    if(setviewType) {
                        setviewType(ViewType.EDIT);
                    }
                }} />}
            {(viewType == ViewType.EDIT || viewType == ViewType.ADD) && <CustomButton
                btnType={ButtonType.SUCCESS}
                title='Save'
                startIcon={<IconDeviceFloppy />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    if(handleSubmit) {
                        handleSubmit();
                    }
                }} />}
        </>
    }

    const renderPreviewData = () => {
        let isVendor = previewData?.[AdminUserField.ROLE]?.[RolesField.TYPE] == RoleTypeEnum.VENDOR_ADMIN;
        let isSysAdmin = formData?.[AdminUserField.ROLE]?.[RolesField.TYPE] == RoleTypeEnum.SUPER_USER;

        return <FieldItemsContainer>
            <FieldItemCard>
                <FieldItemName>{nameProps?.title ?? "--"}</FieldItemName>
                <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.NAME] ?? "--"}</FieldItemValue>
            </FieldItemCard>

            {!isSysAdmin && <>
                <FieldItemCard>
                    <FieldItemName>{registeredCompanyProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.REGISTERED_COMPANY] ?? "--"}</FieldItemValue>
                </FieldItemCard>
                <FieldItemCard>
                    <FieldItemName>Created Date</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.CREATED_DATE] ? DateHelper.getFormattedDate(previewData?.[AdminUserField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{roleProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.ROLE]?.[RolesField.NAME] ?? "--"}</FieldItemValue>
                </FieldItemCard>

                {isVendor && <>
                    <FieldItemCard>
                        {isVendor && <FieldItemName>{vendorProps?.title ?? "--"}</FieldItemName>}
                        {isVendor && <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.VENDORS]?.map((v: Vendor) => { return v?.[VendorField.NAME] })?.join(', ')?.toString() ?? "--"}</FieldItemValue>}
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Last Updated</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.UPDATED_DATE] ? DateHelper.getFormattedDate(previewData?.[AdminUserField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                    </FieldItemCard>

                    <FieldItemCard>
                        <FieldItemName>{taxNumberProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.TAX_NO] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{contPhoneProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CONTACT_NUMBER] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{contEmailAddressProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.EMAIL] ?? "--"}</FieldItemValue>
                    </FieldItemCard>

                    <FieldItemCard>
                        <FieldItemName>{billingAddressProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.FULL_ADDRESS] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{localeProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{DropdownData?.[CachingEnum.PRICE_SETTINGS_TAB_LIST]?.find((item: any) => item.id == previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE])?.name ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{statusProps?.title ?? "--"}</FieldItemName>
                        {StatusHelper.getAdminUserStatus(previewData?.[AdminUserField.STATUS] ?? ActiveStatusEnum.INACTIVE)}
                    </FieldItemCard>
                </>}

                {!isVendor && <>
                    <FieldItemCard>
                        <FieldItemName>{taxNumberProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.TAX_NO] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>Last Updated</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.UPDATED_DATE] ? DateHelper.getFormattedDate(previewData?.[AdminUserField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                    </FieldItemCard>

                    <FieldItemCard>
                        <FieldItemName>{contPhoneProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CONTACT_NUMBER] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{contEmailAddressProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.EMAIL] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{localeProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{DropdownData?.[CachingEnum.PRICE_SETTINGS_TAB_LIST]?.find((item: any) => item.id == previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE])?.name ?? "--"}</FieldItemValue>
                    </FieldItemCard>

                    <FieldItemCard>
                        <FieldItemName>{billingAddressProps?.title ?? "--"}</FieldItemName>
                        <FieldItemValue>{previewData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.FULL_ADDRESS] ?? "--"}</FieldItemValue>
                    </FieldItemCard>
                    <FieldItemCard>
                        <FieldItemName>{statusProps?.title ?? "--"}</FieldItemName>
                        {StatusHelper.getAdminUserStatus(previewData?.[AdminUserField.STATUS] ?? ActiveStatusEnum.INACTIVE)}
                    </FieldItemCard>
                </>}

                
                
            </>}

            {isSysAdmin && <>
                <FieldItemCard>
                    <FieldItemName>{taxNumberProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.TAX_NO] ?? "--"}</FieldItemValue>
                </FieldItemCard>
                
                <FieldItemCard>
                    <FieldItemName>Created Date</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.CREATED_DATE] ? DateHelper.getFormattedDate(previewData?.[AdminUserField.CREATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{roleProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.ROLE]?.[RolesField.NAME] ?? "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{taxNumberProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.TAX_NO] ?? "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>Last Updated</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.UPDATED_DATE] ? DateHelper.getFormattedDate(previewData?.[AdminUserField.UPDATED_DATE], "dd/MM/yyyy hh:mm a") : "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{contPhoneProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CONTACT_NUMBER] ?? "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{statusProps?.title ?? "--"}</FieldItemName>
                    {StatusHelper.getAdminUserStatus(previewData?.[AdminUserField.STATUS] ?? ActiveStatusEnum.INACTIVE)}
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{localeProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{DropdownData?.[CachingEnum.PRICE_SETTINGS_TAB_LIST]?.find((item: any) => item.id == previewData?.[AdminUserField.PROFILE]?.[AdminUserProfileField.CURRENCY_CODE])?.name ?? "--"}</FieldItemValue>
                </FieldItemCard>

                <FieldItemCard>
                    <FieldItemName>{billingAddressProps?.title ?? "--"}</FieldItemName>
                    <FieldItemValue>{previewData?.[AdminUserField.ADDRESS]?.[AdminUserAddressField.FULL_ADDRESS] ?? "--"}</FieldItemValue>
                </FieldItemCard>
            </>}
        </FieldItemsContainer>
    }

    const renderDetailsForm = () => {
        let isVendor = formData?.[AdminUserField.ROLE]?.[RolesField.TYPE] == RoleTypeEnum.VENDOR_ADMIN;
        let isSysAdmin = formData?.[AdminUserField.ROLE]?.[RolesField.TYPE] == RoleTypeEnum.SUPER_USER;

        return <FormFieldItemsContainer>
            <FormTextField {...nameProps} />
            <FormTextField {...billingAddressProps} />

            <RolesDropdown {...roleProps} />
            <FormFieldItemsContainer>
                <FormTextField {...cityProps} />
                <FormTextField {...stateProps} />
            </FormFieldItemsContainer>

            <FormFieldItemsContainer>
                <FormTextField {...contPhoneProps} />
                <FormTextField {...contEmailAddressProps} />
            </FormFieldItemsContainer>
            <FormFieldItemsContainer>
                <FormTextField {...postalCodeProps} />
                <AutoCompleteDropdown {...countryProps} />
            </FormFieldItemsContainer>

            {!isSysAdmin && <>
                <FormFieldItemsContainer>
                    {/*{!isVendor && <CompanyDropdown {...registeredCompanyProps} />}*/}
                    <CompanyDropdown {...registeredCompanyProps} />
                    <FormTextField {...taxNumberProps} />
                </FormFieldItemsContainer>
                <div />

                {isVendor && <VendorsDropdown {...vendorProps} />}
                {isVendor && <div />}

                <FormFieldItemsContainer>
                    <AutoCompleteDropdown {...localeProps} />
                    <AutoCompleteDropdown {...statusProps} />
                </FormFieldItemsContainer>
            </>}

            {isSysAdmin && <>
                <FormFieldItemsContainer>
                    <FormTextField {...taxNumberProps} />
                    <AutoCompleteDropdown {...localeProps} />
                </FormFieldItemsContainer>
                <div />

                <FormFieldItemsContainer>
                    
                    <AutoCompleteDropdown {...statusProps} />
                </FormFieldItemsContainer>
            </>}
        </FormFieldItemsContainer>
    }

    const renderUploadPhoto = () => {
        return <>
            <CustomUploadPhoto
            photoParams={previewData}
            editPhotoParams={formData}
            seteditPhotoParams={setformData}
            viewType={viewType}
            sideTitle={viewType == ViewType.PREVIEW ? formData?.[AdminUserField.USERNAME] ?? "--" : "User Photo"}
            sideSubTitle={viewType == ViewType.PREVIEW ? formData?.[AdminUserField.ROLE]?.[RolesField.NAME] : ""} />
            {/*sideTitle={RouteHelper.getRouteDescription(RouteEnum.ADMIN_USER_DETAILS)} />*/}

        </>

    }

    return <CustomDetailsCard
        // title={RouteHelper.getRouteDescription(RouteEnum.ADMIN_USER_DETAILS)}
        title={renderUploadPhoto()}
        isTitleNotText={true}
        actionButtons={renderActionButtons()} >
            {viewType == ViewType.PREVIEW && renderPreviewData()}
            {viewType != ViewType.PREVIEW && renderDetailsForm()}
        </CustomDetailsCard>
}
